<template>
    <div class="portrait-management-container">
        <!-- 主内容区域 -->
        <div class="main-content">
            <!-- 操作按钮组 -->
            <div class="sub-button-group">
                <button class="sub-button orange-button" @click="openAddDialog">
                    新增画像
                </button>
            </div>

            <!-- 内容区域 -->
            <div class="content-area">
                <!-- 搜索区域 -->
                <div class="search-bar">
                    <el-input v-model="searchForm.portrait_name" placeholder="请输入画像名称" clearable size="large"
                        style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                        <template #prefix>
                            <el-icon>
                                <Search />
                            </el-icon>
                        </template>
                    </el-input>

                    <el-button type="primary" size="large" @click="handleSearch">
                        <el-icon>
                            <Search />
                        </el-icon>
                        搜索
                    </el-button>
                </div>

                <!-- 画像列表 -->
                <el-table :data="portraitList" stripe style="width: 100%; margin-top: 20px" v-loading="loading"
                    class="portrait-table">
                    <el-table-column prop="portrait_name" label="画像名称" width="100" />
                    <el-table-column label="关联用户" width="120">
                        <template #default="{ row }">
                            <div class="user-info">
                                <div class="user-nickname">{{ getUserInfo(row.user_id).nickname }}</div>
                                <div class="user-phone">{{ getUserInfo(row.user_id).phone }}</div>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="company_name" label="公司名称" width="200" />
                    <el-table-column prop="company_short_name" label="公司简称" width="120" />
                    <el-table-column label="创作方向" width="220" show-overflow-tooltip>
                        <template #default="{ row }">
                            <div class="tags-display">
                                <el-tag v-for="(item, index) in getFirstTwoItems(row.creation_direction)" :key="index"
                                    size="small">
                                    {{ item }}
                                </el-tag>
                                <el-tag v-if="getItemCount(row.creation_direction) > 2" size="small" type="info">
                                    +{{ getItemCount(row.creation_direction) - 2 }}
                                </el-tag>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="company_size" label="公司人数" width="100" />
                    <el-table-column label="文案类型" width="250" show-overflow-tooltip>
                        <template #default="{ row }">
                            <div class="tags-display">
                                <el-tag v-for="(item, index) in getFirstTwoItems(row.copywriting_type)" :key="index"
                                    size="small" type="success">
                                    {{ item }}
                                </el-tag>
                                <el-tag v-if="getItemCount(row.copywriting_type) > 2" size="small" type="info">
                                    +{{ getItemCount(row.copywriting_type) - 2 }}
                                </el-tag>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="地区" width="120" show-overflow-tooltip>
                        <template #default="{ row }">
                            <div class="tags-display">
                                <el-tag v-for="(item, index) in getFirstTwoItems(row.regions)" :key="index" size="small"
                                    type="warning">
                                    {{ item }}
                                </el-tag>
                                <el-tag v-if="getItemCount(row.regions) > 2" size="small" type="info">
                                    +{{ getItemCount(row.regions) - 2 }}
                                </el-tag>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="update_time" label="更新时间" width="100" />
                    <el-table-column prop="create_time" label="创建时间" width="100" />

                    <el-table-column label="操作" width="250" fixed="right">
                        <template #default="scope">
                            <el-button type="success" size="small" @click="viewReport(scope.row.id)">
                                <el-icon>
                                    <Document />
                                </el-icon>
                                报表
                            </el-button>

                            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                                <el-icon>
                                    <Edit />
                                </el-icon>
                                编辑
                            </el-button>
                            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                                <el-icon>
                                    <Delete />
                                </el-icon>
                                删除
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页组件 -->
                <div class="pagination-container">
                    <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                        :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                        layout="total, sizes, prev, pager, next, jumper" @size-change="fetchPortraitList"
                        @current-change="fetchPortraitList" />
                </div>
            </div>
        </div>

        <!-- 新增/编辑画像对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="90%" top="-1vh" :close-on-click-modal="false"
            class="portrait-dialog" style="max-width: 1200px;" :before-close="handleDialogClose">
            <div class="dialog-content-wrapper">
                <el-form ref="portraitForm" :model="portraitForm" :rules="formRules" class="portrait-form">
                    <!-- AI生成区域 -->
                    <el-card class="ai-generate-card" shadow="never">
                        <template #header>
                            <div class="card-header">
                                <span>AI智能生成</span>
                                <el-button type="primary" link :loading="aiGenerating" @click="handleAIGenerate"
                                    class="ai-generate-btn">
                                    <el-icon>
                                        <Magic />
                                    </el-icon>
                                    {{ aiGenerating ? '生成中...' : '一键生成' }}
                                </el-button>
                            </div>
                        </template>

                        <el-form-item class="full-width">
                            <span class="form-label">产品描述</span>
                            <div class="ai-input-area">
                                <el-input v-model="aiDescription" type="textarea" :rows="3"
                                    placeholder="请简要描述您的产品或服务，AI将基于此生成完整内容" :disabled="aiGenerating" />
                                <div class="ai-tips">
                                    <el-icon>
                                        <InfoFilled />
                                    </el-icon>
                                    输入产品描述后点击"一键生成内容"，AI将自动填充下方所有内容字段
                                </div>
                            </div>
                        </el-form-item>
                    </el-card>

                    <!-- 第一行：基础信息 -->
                    <div class="form-row">
                        <el-form-item prop="portrait_name" class="form-item">
                            <span class="form-label">画像名称</span>
                            <el-input v-model="portraitForm.portrait_name" placeholder="请输入画像名称（最多20字）" maxlength="20"
                                show-word-limit />
                        </el-form-item>

                        <el-form-item prop="company_name" class="form-item">
                            <span class="form-label">公司名称</span>
                            <el-input v-model="portraitForm.company_name" placeholder="请输入公司名称（最多50字）" maxlength="50"
                                show-word-limit />
                        </el-form-item>

                        <el-form-item prop="company_short_name" class="form-item">
                            <span class="form-label">公司简称</span>
                            <el-input v-model="portraitForm.company_short_name" placeholder="请输入公司简称（最多20字）"
                                maxlength="20" show-word-limit />
                        </el-form-item>
                    </div>

                    <!-- 第二行：创作方向和公司人数 -->
                    <div class="form-row">
                        <el-form-item class="form-item multi-select-item">
                            <span class="form-label">创作方向</span>
                            <el-select v-model="portraitForm.creation_direction" multiple placeholder="请选择创作方向"
                                collapse-tags collapse-tags-tooltip :max-collapse-tags="2" style="width: 100%">
                                <el-option v-for="item in creationDirectionOptions" :key="item.value"
                                    :label="item.label" :value="item.value" />
                            </el-select>
                        </el-form-item>

                        <el-form-item class="form-item">
                            <span class="form-label">公司人数</span>
                            <el-select v-model="portraitForm.company_size" placeholder="请选择公司人数" style="width: 100%">
                                <el-option label="1-10人" value="1-10人" />
                                <el-option label="11-50人" value="11-50人" />
                                <el-option label="51-100人" value="51-100人" />
                                <el-option label="101-500人" value="101-500人" />
                                <el-option label="501-1000人" value="501-1000人" />
                                <el-option label="1000人以上" value="1000人以上" />
                            </el-select>
                        </el-form-item>

                        <el-form-item class="form-item multi-select-item">
                            <span class="form-label">文案类型</span>
                            <el-select v-model="portraitForm.copywriting_type" multiple placeholder="请选择文案类型"
                                collapse-tags collapse-tags-tooltip :max-collapse-tags="2" style="width: 100%">
                                <el-option v-for="item in copywritingTypeOptions" :key="item.value" :label="item.label"
                                    :value="item.value" />
                            </el-select>
                        </el-form-item>
                    </div>

                    <!-- 第三行：地区 -->
                    <div class="form-row">
                        <el-form-item class="form-item full-width">
                            <span class="form-label">地区</span>
                            <div class="region-selector">
                                <el-select v-model="selectedRegion" placeholder="选择或输入地区" filterable allow-create
                                    @change="handleRegionAdd" style="width: 200px; margin-right: 10px">
                                    <el-option v-for="item in commonRegions" :key="item" :label="item" :value="item" />
                                </el-select>
                                <el-button type="primary" @click="handleRegionAdd" size="small">添加</el-button>
                            </div>
                            <div class="selected-regions">
                                <el-tag v-for="(region, index) in portraitForm.regions" :key="index" closable
                                    @close="handleRegionRemove(index)" type="warning" style="margin: 2px 4px;">
                                    {{ region }}
                                </el-tag>
                            </div>
                        </el-form-item>
                    </div>

                    <!-- 产品或服务描述 -->
                    <el-form-item class="full-width">
                        <span class="form-label">产品或服务描述</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.product_service_desc" type="textarea" :rows="4"
                                placeholder="请详细描述公司的产品和服务（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn"
                                @click="showHelp('product_service_desc')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 品牌故事 -->
                    <el-form-item class="full-width">
                        <span class="form-label">品牌故事</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.brand_story" type="textarea" :rows="4"
                                placeholder="请讲述您的品牌故事（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn" @click="showHelp('brand_story')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 产品或服务特点 -->
                    <el-form-item class="full-width">
                        <span class="form-label">产品或服务特点</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.product_features" type="textarea" :rows="4"
                                placeholder="请描述产品或服务的核心特点和优势（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn"
                                @click="showHelp('product_features')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 信任背书 -->
                    <el-form-item class="full-width">
                        <span class="form-label">信任背书</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.trust_endorsement" type="textarea" :rows="4"
                                placeholder="请提供相关的信任背书信息（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn"
                                @click="showHelp('trust_endorsement')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 用户痛点 -->
                    <el-form-item class="full-width">
                        <span class="form-label">用户痛点</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.user_pain_points" type="textarea" :rows="4"
                                placeholder="请描述目标用户的主要痛点和需求（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn"
                                @click="showHelp('user_pain_points')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 客户案例 -->
                    <el-form-item class="full-width">
                        <span class="form-label">客户案例</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.customer_cases" type="textarea" :rows="4"
                                placeholder="请分享成功的客户案例（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn" @click="showHelp('customer_cases')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 补充画像 -->
                    <el-form-item class="full-width">
                        <span class="form-label">补充画像</span>
                        <div class="input-with-help">
                            <el-input v-model="portraitForm.supplement_portrait" type="textarea" :rows="4"
                                placeholder="请补充其他重要的画像信息（最多1000字）" maxlength="1000" show-word-limit />
                            <el-button type="text" class="help-btn"
                                @click="showHelp('supplement_portrait')">查看说明</el-button>
                        </div>
                    </el-form-item>

                    <!-- 协议和IP地址 -->
                    <div class="form-row">
                        <el-form-item class="form-item">
                            <el-checkbox v-model="portraitForm.agreement_accepted">
                                我已阅读并同意 <el-button type="text" class="agreement-link"
                                    @click="showAgreement">《信息安全协议》</el-button>
                            </el-checkbox>
                        </el-form-item>

                        <el-form-item class="form-item">
                            <span class="form-label">IP地址 <span class="ip-address">{{ currentIp }}</span> </span>
                        </el-form-item>
                    </div>
                </el-form>
            </div>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitPortraitForm" :loading="submitting">保存提交</el-button>
            </template>
        </el-dialog>

        <!-- 查看说明对话框 -->
        <el-dialog v-model="helpDialogVisible" :title="helpTitle" width="600px" class="help-dialog">
            <div class="help-content">
                <div class="help-section">
                    <span class="help-label">内容：</span>
                    <span class="help-text content-text">{{ helpContent.content }}</span>
                </div>
                <div class="help-section">
                    <span class="help-label">说明：</span>
                    <span class="help-text description-text">{{ helpContent.description }}</span>
                </div>
                <div class="help-section">
                    <span class="help-label">实例：</span>
                    <div class="example-text">{{ helpContent.example }}</div>
                </div>
            </div>
            <template #footer>
                <el-button type="primary" @click="helpDialogVisible = false">知道了</el-button>
            </template>
        </el-dialog>

        <!-- 信息安全协议对话框 -->
        <el-dialog v-model="agreementDialogVisible" title="信息安全协议" width="700px" class="agreement-dialog">
            <div class="agreement-content">
                <h3 class="agreement-main-title">信息内容填写规则</h3>

                <h4 class="agreement-subtitle">一、总则</h4>
                <p class="agreement-paragraph">
                    为保障平台运营秩序，维护各方合法权益，确保客户提交信息的真实、合法、合规，防范知识产权侵权等风险，特制定本规则。客户在平台提交任何信息，即视为已阅读、理解并同意遵守本规则。</p>

                <h4 class="agreement-subtitle">二、信息真实性要求</h4>
                <p class="agreement-paragraph">1. 客户提交的信息需真实、准确、完整，不得存在虚假记载、误导性陈述或重大遗漏。</p>
                <p class="agreement-paragraph">2. 涉及个人信息的，需确保信息为本人真实信息或已获得信息主体的合法授权；涉及企业信息的，需提供真实有效的营业执照、资质证书等相关证明材料。
                </p>
                <p class="agreement-paragraph">3. 提交的内容需与实际情况一致，不得伪造、篡改数据、证明文件及其他相关信息。</p>

                <h4 class="agreement-subtitle">三、合法合规性要求</h4>
                <p class="agreement-paragraph">1. 提交的信息需符合国家法律法规、行业监管规定及平台相关协议，不得违反公序良俗。</p>
                <p class="agreement-paragraph">2. 不得提交危害国家安全、损害社会公共利益、破坏民族团结、宣扬淫秽色情、暴力恐怖等违法违规内容。</p>
                <p class="agreement-paragraph">3. 不得提交涉及赌博、诈骗、传销、非法金融等违法犯罪活动的信息。</p>
                <p class="agreement-paragraph">4. 涉及特殊行业（如医疗、教育、金融等）的信息，需具备相应的行业资质并符合专项监管要求。</p>

                <h4 class="agreement-subtitle">四、知识产权保护要求</h4>
                <p class="agreement-paragraph">1. 客户需确保所提交信息未侵犯任何第三方的知识产权，包括但不限于著作权、商标权、专利权、商业秘密等。</p>
                <p class="agreement-paragraph">2. 若提交的信息涉及第三方知识产权，需提前获得权利人的书面授权，并在提交时附上完整的授权证明材料。</p>
                <p class="agreement-paragraph">3. 不得提交盗版、抄袭、剽窃他人的作品或未经授权使用他人知识产权的内容。</p>

                <h4 class="agreement-subtitle">五、免责声明</h4>
                <p class="agreement-paragraph">1. 平台仅对客户提交信息进行形式审核，不保证信息的绝对真实性及完整性，客户需对自身提交信息承担全部责任。</p>
                <p class="agreement-paragraph">2. 因客户提交信息违反本规则导致的任何纠纷、处罚或损失，均由客户自行承担，与平台无关。</p>
            </div>
            <template #footer>
                <el-button @click="agreementDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="handleAgreementConfirm">我已阅读并同意</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';

import {
    Search, Edit, Delete, Magic, InfoFilled, Document
} from '@element-plus/icons-vue'

export default {
    name: 'PortraitManagement',
    components: { Search, Edit, Delete, Magic, InfoFilled, Document },
    data() {
        return {
            loading: false,
            submitting: false,
            aiGenerating: false,
            // 对话框控制
            dialogVisible: false,
            helpDialogVisible: false,
            agreementDialogVisible: false,
            dialogTitle: '新增画像',
            isEditMode: false,
            userList: [], // 用户列表
            userMap: {},  // 用户ID映射
            userLoading: false,

            // AI生成相关
            aiDescription: '',

            // 搜索表单
            searchForm: {
                portrait_name: ''
            },

            // 画像表单
            portraitForm: {
                id: null,
                portrait_name: '',
                company_name: '',
                company_short_name: '',
                creation_direction: [],
                company_size: '',
                product_service_desc: '',
                copywriting_type: [],
                brand_story: '',
                product_features: '',
                trust_endorsement: '',
                user_pain_points: '',
                customer_cases: '',
                supplement_portrait: '',
                regions: [],
                agreement_accepted: false
            },

            // 表单验证规则
            formRules: {
                portrait_name: [
                    { required: true, message: '请输入画像名称', trigger: 'blur' },
                    { min: 1, max: 20, message: '画像名称长度在1-20个字符', trigger: 'blur' }
                ],
                company_name: [
                    { required: true, message: '请输入公司名称', trigger: 'blur' },
                    { min: 1, max: 50, message: '公司名称长度在1-50个字符', trigger: 'blur' }
                ],
                company_short_name: [
                    { required: true, message: '请输入公司简称', trigger: 'blur' },
                    { min: 1, max: 20, message: '公司简称长度在1-20个字符', trigger: 'blur' }
                ]
            },

            // 画像列表
            portraitList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 选项数据
            creationDirectionOptions: [
                { value: '经验分享', label: '经验分享' },
                { value: '过程分享', label: '过程分享' },
                { value: '用户痛点', label: '用户痛点' },
                { value: '用户避坑', label: '用户避坑' },
                { value: '科技前沿', label: '科技前沿' },
                { value: '行业洞察', label: '行业洞察' },
                { value: '潮流趋势', label: '潮流趋势' },
                { value: '情感触动', label: '情感触动' },
                { value: '产品工艺', label: '产品工艺' },
                { value: '产品特色', label: '产品特色' },
                { value: '技术创新', label: '技术创新' },
                { value: '功能亮点', label: '功能亮点' },
                { value: '成本控制', label: '成本控制' },
                { value: '合作案例', label: '合作案例' },
                { value: '案例分析', label: '案例分析' },
                { value: '企业实力', label: '企业实力' },
                { value: '人才培养', label: '人才培养' },
                { value: '情感煽动', label: '情感煽动' },
                { value: '热点关联', label: '热点关联' },
                { value: '夸张方式', label: '夸张方式' },
                { value: '效率提升', label: '效率提升' },
                { value: '行业对比', label: '行业对比' },
                { value: '成长优化', label: '成长优化' },
                { value: '风险揭示', label: '风险揭示' },
                { value: '原则强调', label: '原则强调' },
                { value: '数据应用', label: '数据应用' },
                { value: '新手引导', label: '新手引导' },
                { value: '法规解读', label: '法规解读' },
                { value: '总结推荐', label: '总结推荐' }
            ],

            copywritingTypeOptions: [
                { value: '产品技术解读型', label: '产品技术解读型' },
                { value: '热点话题关联型', label: '热点话题关联型' },
                { value: '技术参数与性能评测型', label: '技术参数与性能评测型' },
                { value: '生活场景与情感共鸣型', label: '生活场景与情感共鸣型' },
                { value: '场景代入型', label: '场景代入型' },
                { value: '情感共鸣型', label: '情感共鸣型' },
                { value: '体验分享型', label: '体验分享型' },
                { value: '创意故事型', label: '创意故事型' },
                { value: '悬念吸引型', label: '悬念吸引型' },
                { value: '对比评测型', label: '对比评测型' },
                { value: '新闻报道型', label: '新闻报道型' },
                { value: '知识科普型', label: '知识科普型' },
                { value: '行业问答型', label: '行业问答型' },
                { value: '行业百科型', label: '行业百科型' },
                { value: '活动推广型', label: '活动推广型' },
                { value: '用户对话型', label: '用户对话型' }
            ],

            // 地区选择
            selectedRegion: '',
            commonRegions: [
                '北京', '上海', '广州', '深圳', '杭州', '南京', '武汉', '成都', '重庆', '西安',
                '天津', '苏州', '宁波', '青岛', '郑州', '长沙', '福州', '厦门', '沈阳', '大连'
            ],

            // 帮助内容
            helpTitle: '',
            helpContent: {
                content: '',
                description: '',
                example: ''
            },

            // IP地址
            currentIp: '获取中...'
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.fetchPortraitList();
        this.getClientIp();
        this.fetchUserList();
    },
    methods: {

        // 查看画像报表
        viewReport(id) {
            // 构建报表URL
            const currentDomain = window.location.origin;
            const reportUrl = `${currentDomain}/site/api/report.html?id=${id}`;

            // 在新窗口打开报表页面
            window.open(reportUrl, '_blank');
        },

        // AI生成内容
        async handleAIGenerate() {
            if (!this.aiDescription.trim()) {
                this.$message.warning('请输入产品描述');
                return;
            }

            this.aiGenerating = true;
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/ai_generate_portrait.php`,
                    {
                        action: 'generate_portrait_content',
                        store_id: this.userInfo.id,
                        user_id: this.userInfo.userId || 0,
                        description: this.aiDescription
                    }
                );

                if (response.data.success) {
                    const aiData = response.data.data;

                    // 自动填充表单
                    this.portraitForm.product_service_desc = aiData.product_service_desc || '';
                    this.portraitForm.brand_story = aiData.brand_story || '';
                    this.portraitForm.product_features = aiData.product_features || '';
                    this.portraitForm.trust_endorsement = aiData.trust_endorsement || '';
                    this.portraitForm.user_pain_points = aiData.user_pain_points || '';
                    this.portraitForm.customer_cases = aiData.customer_cases || '';
                    this.portraitForm.supplement_portrait = aiData.supplement_portrait || '';

                    this.$message.success('AI内容生成成功！请检查并完善相关信息。');
                } else {
                    throw new Error(response.data.error || '生成失败');
                }
            } catch (error) {
                console.error('AI生成失败:', error);
                this.$message.error('AI生成失败: ' + (error.response?.data?.error || error.message));
            } finally {
                this.aiGenerating = false;
            }
        },

        // 获取客户端IP
        async getClientIp() {
            const ipServices = [
                'https://api.ipify.org?format=json',
                'https://api64.ipify.org?format=json',
                'https://ipinfo.io/json',
                'https://ipapi.co/json',
                'https://www.cloudflare.com/cdn-cgi/trace'
            ];

            for (let service of ipServices) {
                try {
                    const response = await axios.get(service, { timeout: 5000 });

                    if (service.includes('cloudflare')) {
                        // 处理 cloudflare 的文本响应
                        const text = response.data;
                        const ipMatch = text.match(/ip=([\d.]+)/);
                        if (ipMatch) {
                            this.currentIp = ipMatch[1];
                            return;
                        }
                    } else {
                        // 处理 JSON 响应
                        const data = response.data;
                        if (data.ip) {
                            this.currentIp = data.ip;
                            return;
                        }
                    }
                } catch (error) {
                    console.log(`IP服务 ${service} 失败:`, error.message);
                    continue;
                }
            }

            // 所有服务都失败时的备选方案
            try {
                // 尝试从后端获取
                const { data } = await axios.get(`${config.BASE_URL}/site/api/get_ip.php`);
                if (data.ip) {
                    this.currentIp = data.ip;
                    return;
                }
            } catch (error) {
                console.log('后端IP服务失败:', error.message);
            }

            // 最终备选方案
            this.currentIp = '127.0.0.1';
        },
        // 获取用户列表
        async fetchUserList() {
            this.userLoading = true
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/site/api/associated_user.php`,
                    {
                        action: 'get_users',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.userList = response.data.data
                    // 构建用户映射
                    this.userMap = {}
                    response.data.data.forEach(user => {
                        this.userMap[user.id] = user
                    })
                }
            } catch (error) {
                console.error('获取用户列表错误:', error)
            } finally {
                this.userLoading = false
            }
        },

        // 根据user_id获取用户信息
        getUserInfo(userId) {
            const user = this.userMap[userId]
            if (user) {
                return {
                    nickname: user.nickname,
                    phone: user.phone,
                    avatar: user.avatar
                }
            }
            return {
                nickname: '管理员',
                phone: '',
                avatar: ''
            }
        },
        // 获取画像列表
        async fetchPortraitList() {
            this.loading = true;
            try {
                const params = {
                    action: this.searchForm.portrait_name ? 'search' : 'list',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };

                if (this.searchForm.portrait_name) {
                    params.portrait_name = this.searchForm.portrait_name;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/site/api/portrait_management.php`,
                    params
                );

                if (data.data) {
                    this.portraitList = data.data.map(item => ({
                        ...item,
                        creation_direction: this.parseJsonField(item.creation_direction),
                        copywriting_type: this.parseJsonField(item.copywriting_type),
                        regions: this.parseJsonField(item.regions)
                    }));
                    this.pagination.total = data.pagination?.total || data.data.length;
                }
            } catch (error) {
                this.$message.error('获取画像列表失败');
                console.error('获取画像列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 解析JSON字段
        parseJsonField(field) {
            if (!field) return [];
            try {
                if (typeof field === 'string') {
                    return JSON.parse(field);
                }
                return field;
            } catch (error) {
                console.warn('JSON解析失败:', field);
                return [];
            }
        },

        // 处理搜索
        handleSearch() {
            this.pagination.page = 1;
            this.fetchPortraitList();
        },

        // 打开新增对话框
        openAddDialog() {
            this.dialogTitle = '新增画像';
            this.isEditMode = false;
            this.portraitForm = {
                id: null,
                portrait_name: '',
                company_name: '',
                company_short_name: '',
                creation_direction: [],
                company_size: '',
                product_service_desc: '',
                copywriting_type: [],
                brand_story: '',
                product_features: '',
                trust_endorsement: '',
                user_pain_points: '',
                customer_cases: '',
                supplement_portrait: '',
                regions: [],
                agreement_accepted: false
            };
            this.aiDescription = '';
            this.dialogVisible = true;
        },

        // 打开编辑对话框
        openEditDialog(row) {
            this.dialogTitle = '编辑画像';
            this.isEditMode = true;
            this.portraitForm = {
                ...row,
                creation_direction: this.parseJsonField(row.creation_direction),
                copywriting_type: this.parseJsonField(row.copywriting_type),
                regions: this.parseJsonField(row.regions),
                agreement_accepted: true // 编辑时自动勾选协议
            };
            this.aiDescription = '';
            this.dialogVisible = true;
        },

        // 对话框关闭处理
        handleDialogClose() {
            this.dialogVisible = false;
            this.$refs.portraitForm?.clearValidate();
        },

        // 提交画像表单
        submitPortraitForm() {
            this.$refs.portraitForm.validate(async (valid) => {
                if (!valid) {
                    this.$message.error('请完善必填信息');
                    return;
                }

                if (!this.portraitForm.agreement_accepted) {
                    this.$message.error('请阅读并同意信息安全协议');
                    return;
                }

                this.submitting = true;
                try {
                    const action = this.isEditMode ? 'update' : 'add';
                    const payload = {
                        ...this.portraitForm,
                        store_id: this.userInfo.id,
                        creation_direction: JSON.stringify(this.portraitForm.creation_direction),
                        copywriting_type: JSON.stringify(this.portraitForm.copywriting_type),
                        regions: JSON.stringify(this.portraitForm.regions)
                    };

                    // 编辑时需要id
                    if (this.isEditMode) {
                        payload.id = this.portraitForm.id;
                    }

                    const { data } = await axios.post(
                        `${config.BASE_URL}/site/api/portrait_management.php`,
                        {
                            action,
                            ...payload
                        }
                    );

                    if (data.success) {
                        this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
                        this.dialogVisible = false;
                        this.fetchPortraitList();
                    } else {
                        this.$message.error(data.error || '操作失败');
                    }
                } catch (error) {
                    const errorMsg = error.response?.data?.error || error.message || '操作失败';
                    this.$message.error(`操作失败: ${errorMsg}`);
                    console.error('画像表单提交错误:', error);
                } finally {
                    this.submitting = false;
                }
            });
        },

        // 删除画像
        async handleDelete(id) {
            this.$confirm('确定要删除该画像吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const { data } = await axios.post(
                        `${config.BASE_URL}/site/api/portrait_management.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.userInfo.id
                        }
                    );

                    if (data.success) {
                        this.$message.success('删除成功');
                        this.fetchPortraitList();
                    } else {
                        this.$message.error(data.error || '删除失败');
                    }
                } catch (error) {
                    const errorMsg = error.response?.data?.error || error.message || '删除失败';
                    this.$message.error(`删除失败: ${errorMsg}`);
                    console.error('画像删除错误:', error);
                }
            }).catch(() => { });
        },

        // 地区操作
        handleRegionAdd() {
            if (this.selectedRegion && !this.portraitForm.regions.includes(this.selectedRegion)) {
                this.portraitForm.regions.push(this.selectedRegion);
                this.selectedRegion = '';
            }
        },

        handleRegionRemove(index) {
            this.portraitForm.regions.splice(index, 1);
        },

        // 显示帮助说明
        showHelp(field) {
            const helpMap = {
                product_service_desc: {
                    title: '产品或服务描述说明',
                    content: '产品介绍、服务介绍',
                    description: '全面介绍公司的业务范围和各类产品，对于每种产品，用通俗易懂的语言描述其功能和用途，避免过多专业术语，突出产品的实际应用场景和价值。',
                    example: 'XX科技公司在智能制造领域拥有多年经验，业务涵盖工业自动化、智能装备研发等多个方向。我们的核心产品智能机械臂，采用先进的运动控制算法，能够实现高精度定位和柔性生产，广泛应用于电子装配、汽车制造等行业，帮助企业提升生产效率30%以上，降低人工成本40%。'
                },
                product_features: {
                    title: '产品或服务特点说明',
                    content: '主营产品优势介绍或服务优势介绍',
                    description: '重点突出产品的独特优势和核心卖点，可以通过性能对比、客户案例、数据验证等方式，让AI充分理解产品的差异化价值和技术领先性。',
                    example: '我们的智能检测系统采用AI视觉识别技术，相比传统人工检测，准确率从85%提升至99.5%，检测速度提高3倍。某电子制造企业使用后，产品不良率降低70%，每年节省质量成本约200万元，投资回报周期仅6个月。'
                },
                trust_endorsement: {
                    title: '信任背书说明',
                    content: '与哪些客户有过合作',
                    description: '列举合作过的知名企业或机构，展示公司的行业地位和客户认可度，包括获得的资质认证、行业奖项等重要背书信息。',
                    example: '公司已通过ISO9001质量管理体系认证和ISO14001环境管理体系认证，产品符合CE、UL等国际标准。长期为华为、比亚迪、美的等知名企业提供配套服务，在多个重大项目中获得客户好评，建立了稳定的战略合作关系。'
                },
                user_pain_points: {
                    title: '用户痛点说明',
                    content: '包含行业痛点、用户痛点等',
                    description: '从客户实际需求和行业现状出发，分析目标用户面临的主要问题和挑战，可以从成本、效率、质量、技术等多个维度进行深入剖析。',
                    example: '1、传统生产方式人工成本高，熟练技工难招聘，人力成本年增15%以上；2、设备自动化程度低，生产效率难以突破，订单交付周期长影响客户满意度；3、产品质量稳定性差，返工率高，直接导致利润空间被压缩。'
                },
                customer_cases: {
                    title: '客户案例说明',
                    content: '包含用户场景、用户需求等',
                    description: '通过具体的客户合作案例，展示产品在实际应用中的效果和价值，包括客户背景、面临问题、解决方案、实施效果等完整信息。',
                    example: '为某大型家电企业提供的自动化生产线改造方案，帮助客户实现了生产流程的数字化管理。改造后，生产线效率提升35%，产品不良率从5%降至1.2%，人力成本降低50%，客户在一年内即收回投资成本，并获得行业创新奖项。'
                },
                brand_story: {
                    title: '品牌故事说明',
                    content: '品牌发展历程和企业文化',
                    description: '讲述公司的创立背景、发展历程、核心理念和文化价值观，通过真实的故事展现企业的使命感和责任感。',
                    example: '公司创立于2015年，由几位资深工程师共同创办。创业初期，我们发现在传统制造业中存在大量效率低下的问题，于是决心通过技术创新改变这一现状。经过多年研发，我们成功开发出多款智能装备，已服务超过500家企业，帮助客户创造价值超过10亿元。'
                },
                supplement_portrait: {
                    title: '补充画像说明',
                    content: '其他重要的企业信息',
                    description: '补充说明企业的技术实力、研发能力、服务特色、未来规划等有助于全面了解企业的重要信息。',
                    example: '公司拥有30多项专利技术，研发团队占员工总数的40%，每年投入营业收入的15%用于技术研发。建立了完善的售后服务体系，提供7×24小时技术支持，确保客户设备稳定运行。未来计划拓展海外市场，为全球客户提供优质服务。'
                }
            };

            const helpInfo = helpMap[field];
            if (helpInfo) {
                this.helpTitle = helpInfo.title;
                this.helpContent = helpInfo;
                this.helpDialogVisible = true;
            }
        },

        // 显示协议
        showAgreement() {
            this.agreementDialogVisible = true;
        },

        // 修改协议对话框的关闭处理
        handleAgreementConfirm() {
            this.portraitForm.agreement_accepted = true;
            this.agreementDialogVisible = false;
        },

        // 工具方法：获取前两个项目用于显示
        getFirstTwoItems(items) {
            if (!Array.isArray(items) || items.length === 0) return [];
            return items.slice(0, 2);
        },

        // 工具方法：获取项目数量
        getItemCount(items) {
            if (!Array.isArray(items)) return 0;
            return items.length;
        }
    }
}
</script>

<style scoped lang="scss">
.portrait-management-container {
    min-height: calc(100vh - 160px);
    background: transparent;
}

.main-content {
    background: hsla(0, 0%, 100%, .1);
    border-radius: 8px;
    padding: 24px;
    backdrop-filter: blur(10px);
}

.button-group {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.main-button {
    padding: 12px 24px;
    background: #ff7048;
    border: 1px solid #ff7048;
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #e55a3a;
        border-color: #e55a3a;
        transform: translateY(-2px);
    }

    &.active {
        background: #ff7048;
        border-color: #ff7048;
    }
}

.sub-button-group {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    align-items: center;
    flex-wrap: wrap;
}

.sub-button {
    padding: 8px 16px;
    background: #ff7048;
    border: 1px solid #ff7048;
    border-radius: 6px;
    color: #ffffff;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #e55a3a;
        border-color: #e55a3a;
    }

    &.orange-button {
        background: #ff7048;
        border-color: #ff7048;

        &:hover {
            background: #e55a3a;
            border-color: #e55a3a;
        }
    }
}

.content-area {
    min-height: 400px;
}

.search-bar {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* AI生成卡片样式 */
.ai-generate-card {
    margin-bottom: 20px;
    border: 1px solid #e6a23c;
    background: rgba(230, 162, 60, 0.05);
    color: #ffffff;

    :deep(.el-card__header) {
        border-bottom: 1px solid #e6a23c;
        padding: 12px 20px;
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .ai-generate-btn {
            color: #ffffff;
            font-weight: 600;
            padding: 5px;

            &:hover {
                color: #b88230;
            }
        }
    }
}

.ai-input-area {
    position: relative;

    .ai-tips {
        margin-top: 8px;
        padding: 8px 12px;
        background: rgba(230, 162, 60, 0.1);
        border-radius: 4px;
        border-left: 3px solid #e6a23c;
        color: #e6a23c;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 6px;

        .el-icon {
            font-size: 14px;
        }
    }
}

/* 表格样式 */
.portrait-table {
    border: 1px solid rgba(255, 112, 72, 0.3);
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table) {
        background: transparent;

        th {
            background: rgba(255, 112, 72, 0.1) !important;
            color: #e0e6ff !important;
            border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
        }

        tr {
            background: rgba(255, 255, 255, 0.05);

            &:hover {
                background: rgba(255, 255, 255, 0.1) !important;
            }
        }

        td {
            color: #e0e6ff !important;
            border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
        }
    }
}

.tags-display {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

/* 对话框内容包装器 */
.dialog-content-wrapper {
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 10px;
}

.portrait-form {
    padding: 10px 0;
}

/* 表单样式 */
.form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    align-items: flex-start;
}

.form-item {
    flex: 1;
    margin-bottom: 0;
    position: relative;
}

.full-width {
    width: 100%;
}

/* 自定义表单标签 */
.form-label {
    display: block;
    color: #ffffff !important;
    font-weight: 500;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 1.4;
}

.multi-select-item {
    min-height: 80px;
}

.region-selector {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

.selected-regions {
    min-height: 32px;
    padding: 8px;
    border: 1px solid rgba(255, 112, 72, 0.3);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.05);
}

.input-with-help {
    position: relative;

    .help-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        color: #ff7048;
        z-index: 10;
        font-size: 12px;
    }
}

.ip-address {
    color: #e0e6ff;
    font-family: monospace;
    background: rgba(255, 255, 255, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
    margin-top: 8px;
}

/* 帮助内容样式 */
.help-content {
    line-height: 1.6;
}

.help-section {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
}

.help-label {
    color: #ff7048;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.help-text {
    color: #e0e6ff;
    line-height: 1.5;
}

.content-text {
    color: #ffd700;
    font-weight: 500;
}

.description-text {
    color: #a0e8ff;
}

.example-text {
    margin-top: 8px;
    padding: 12px;
    background: rgba(255, 112, 72, 0.1);
    border-radius: 4px;
    border-left: 3px solid #ff7048;
    font-style: italic;
    line-height: 1.5;
    color: #e0e6ff;
}

/* 协议内容样式 */
.agreement-content {
    max-height: 400px;
    overflow-y: auto;
    line-height: 1.6;
}

.agreement-main-title {
    color: #ff7048;
    text-align: center;
    margin-bottom: 20px;
    font-size: 20px;
}

.agreement-subtitle {
    color: #ffd700;
    margin: 16px 0 8px 0;
    font-size: 16px;
}

.agreement-paragraph {
    color: #e0e6ff;
    margin-bottom: 12px;
    text-indent: 2em;
    line-height: 1.6;
}

.agreement-link {
    color: #ff7048 !important;
    font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .portrait-management-container {
        padding: 16px;
    }

    .form-row {
        flex-direction: column;
        gap: 0;
    }

    .form-item {
        margin-bottom: 20px;
    }

    .button-group {
        gap: 12px;

        .main-button {
            padding: 10px 20px;
            font-size: 13px;
        }
    }

    .sub-button-group {
        gap: 8px;

        .sub-button {
            padding: 6px 12px;
            font-size: 12px;
        }
    }

    .dialog-content-wrapper {
        max-height: 60vh;
    }
}
</style>

<style>
/* 修复 Element Plus 默认样式影响 */
.portrait-dialog .el-form-item__content {
    display: block !important;
    align-items: normal !important;
    flex-wrap: nowrap !important;
    line-height: normal !important;
    min-width: auto !important;
}

.portrait-dialog .el-form-item {
    display: block !important;
    margin-bottom: 20px;
}

.portrait-dialog .el-form-item__label {
    color: #ffffff !important;
    font-weight: 500;
    margin-bottom: 8px;
    display: block !important;
    float: none !important;
    text-align: left !important;
    line-height: 1.4 !important;
    padding: 0 !important;
}

/* 多选标签样式优化 */
.portrait-dialog .el-select__tags {
    flex-wrap: nowrap !important;
    overflow: hidden !important;
    max-height: 60px;
    overflow-y: auto;
}

.portrait-dialog .el-select .el-tag {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 2px 4px 2px 0;
    background: rgba(255, 112, 72, 0.2) !important;
    border-color: rgba(255, 112, 72, 0.5) !important;
    color: #ff7048 !important;
}

/* 文本域表单项特殊处理 */
.portrait-dialog .el-form-item.full-width .el-form-item__content {
    display: block !important;
}

.portrait-dialog .el-textarea .el-textarea__inner {
    min-height: 100px;
    resize: vertical;
    color: #ffffff !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.portrait-dialog .el-textarea .el-textarea__inner::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

/* 多选下拉框的弹出层样式 */
.portrait-dialog .el-select-dropdown {
    max-height: 300px;
    overflow-y: auto;
    background: rgba(13, 5, 26, 0.95) !important;
    border: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.portrait-dialog .el-select-dropdown__item {
    padding: 8px 16px;
    color: #e0e6ff !important;
}

.portrait-dialog .el-select-dropdown__item:hover {
    background: rgba(255, 112, 72, 0.1) !important;
    color: #ff7048 !important;
}

.portrait-dialog .el-select-dropdown__item.selected {
    background: rgba(255, 112, 72, 0.2) !important;
    color: #ff7048 !important;
}

/* 对话框全局样式 */
.portrait-dialog .el-dialog,
.help-dialog .el-dialog,
.agreement-dialog .el-dialog {
    background: rgba(13, 5, 26, 0.95) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 112, 72, 0.3);
    border-radius: 16px;
}

.portrait-dialog .el-dialog__header,
.help-dialog .el-dialog__header,
.agreement-dialog .el-dialog__header {
    margin: 0 !important;
    padding: 20px !important;
    border-bottom: 1px solid rgba(255, 112, 72, 0.2);
    background: transparent !important;
}

.portrait-dialog .el-dialog__title,
.help-dialog .el-dialog__title,
.agreement-dialog .el-dialog__title {
    color: #ffffff !important;
    font-weight: 600;
    font-size: 18px;
}

.portrait-dialog .el-dialog__body,
.help-dialog .el-dialog__body,
.agreement-dialog .el-dialog__body {
    padding: 20px !important;
    background: transparent !important;
}

.portrait-dialog .el-dialog__footer,
.help-dialog .el-dialog__footer,
.agreement-dialog .el-dialog__footer {
    border-top: 1px solid rgba(255, 112, 72, 0.2) !important;
    padding: 16px 20px !important;
    margin: 0 !important;
    background: rgba(255, 112, 72, 0.05);
}

/* 协议对话框按钮样式优化 */
.agreement-dialog .el-dialog__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.agreement-dialog .el-button {
    min-width: 100px;
}

/* 表单元素样式 */
.portrait-dialog .el-input__inner {
    color: #ffffff !important;
}


.portrait-dialog .el-textarea__inner,
.portrait-dialog .el-select .el-input__inner {
    color: #ffffff !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.portrait-dialog .el-input__inner::placeholder,
.portrait-dialog .el-textarea__inner::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

.portrait-dialog .el-input__wrapper:hover,
.portrait-dialog .el-textarea__inner:hover {
    border-color: #ff7048 !important;
}

.portrait-dialog .el-input__wrapper.is-focus,
.portrait-dialog .el-textarea__inner:focus {
    border-color: #ff7048 !important;
    box-shadow: 0 0 0 1px #ff7048 !important;
}

/* 复选框样式 */
.portrait-dialog .el-checkbox__label {
    color: #e0e6ff !important;
}

.portrait-dialog .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #ff7048 !important;
    border-color: #ff7048 !important;
}

/* 按钮样式 */
.portrait-dialog .el-button,
.help-dialog .el-button,
.agreement-dialog .el-button {
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.portrait-dialog .el-button--primary,
.help-dialog .el-button--primary,
.agreement-dialog .el-button--primary {
    background: #ff7048 !important;
    border-color: #ff7048 !important;
}

.portrait-dialog .el-button--primary:hover,
.help-dialog .el-button--primary:hover,
.agreement-dialog .el-button--primary:hover {
    background: #e55a3a !important;
    border-color: #e55a3a !important;
    transform: translateY(-1px);
}

/* 分页样式 */
.el-pagination {
    --el-pagination-bg-color: transparent !important;
    --el-pagination-text-color: #e0e6ff !important;
    --el-pagination-button-color: #e0e6ff !important;
    --el-pagination-button-disabled-bg-color: transparent !important;
    --el-pagination-hover-color: #ff7048 !important;
}

.el-pagination .btn-prev,
.el-pagination .btn-next,
.el-pagination .number {
    background: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 112, 72, 0.3) !important;
    color: #e0e6ff !important;
}

.el-pagination .number.active {
    background: #ff7048 !important;
    border-color: #ff7048 !important;
    color: #ffffff !important;
}

.el-pagination .number:hover {
    color: #ff7048 !important;
}

/* 滚动条样式 */
.dialog-content-wrapper::-webkit-scrollbar {
    width: 6px;
}

.dialog-content-wrapper::-webkit-scrollbar-track {
    background: rgba(255, 112, 72, 0.1);
    border-radius: 3px;
}

.dialog-content-wrapper::-webkit-scrollbar-thumb {
    background: #ff7048;
    border-radius: 3px;
}

.dialog-content-wrapper::-webkit-scrollbar-thumb:hover {
    background: #e55a3a;
}

.agreement-content::-webkit-scrollbar {
    width: 6px;
}

.agreement-content::-webkit-scrollbar-track {
    background: rgba(255, 112, 72, 0.1);
    border-radius: 3px;
}

.agreement-content::-webkit-scrollbar-thumb {
    background: #ff7048;
    border-radius: 3px;
}

.agreement-content::-webkit-scrollbar-thumb:hover {
    background: #e55a3a;
}

.user-info {
    .user-nickname {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .user-phone {
        color: #909399;
        font-size: 12px;
    }
}
</style>